<template>
  <div :id="'product-carousel-' + id" class="product-carousel-container" :class="[swiperMargin && 'swiper-margin']">
    <h2 v-if="title" class="product-carousel-title light">{{ title }}</h2>

    <div ref="swiper" class="swiper-container" :style="[carouselPadding ? { padding: `0px ${carouselPadding}px` } : {}]">
      <div v-show="toShow" class="swiper-wrapper">
        <div v-for="product in displayedProducts" :key="product.id" class="swiper-slide" :data-product-id="product.id">
          <Product
            :product="product"
            :show-variations="false"
            :space-bottom="false"
            :badge="!onSaleFilter"
            :open-in-new-tab="openInNewTab"
            @click="onProductClick"
          >
            <template v-if="showAddToCartBtn" v-slot:responsiveCartButton>
              <AddToCartButton
                :products="parseProduct(product)"
                :show-loader="!animateCartBtn"
                :button-classes="['outlined', 'coral']"
                :scroll-first="false"
                class="add-to-cart-button"
                :animate="animateCartBtn"
                @added="onProductAddedToCart(product)"
              >
                <span class="af af-plus"></span>&nbsp;<span class="af af-cart"></span>
              </AddToCartButton>
            </template>

            <template v-slot:customTitle>
              <slot name="customProductTitle" :productTitle="product.title"></slot>
            </template>
          </Product>
        </div>
      </div>
      <div class="swiper-button-next" :class="[smallArrows && 'small-arrow', !swiperMargin && 'initial-position']">
        <span class="af af-chevron-right"></span>
      </div>
      <div class="swiper-button-prev" :class="[smallArrows && 'small-arrow', !swiperMargin && 'initial-position']">
        <span class="af af-chevron-left"></span>
      </div>
      <div v-if="showPagination" ref="pagination" class="swiper-pagination"></div>
    </div>
  </div>
</template>

<script>
import Product from '@/components/commons/Product.vue';
import ImportUtils from '@/components/commons/ImportUtils.js';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import Tracking from './Tracking.js';

const slideBreakpointKeys = ['slidesPerView', 'spaceBetween'];

const validateSlideBreakpoints = (breakpoints) =>
  Object.keys(breakpoints).every((key) => slideBreakpointKeys.every((expectedKey) => expectedKey in breakpoints[key]));

export default {
  name: 'ProductCarousel',
  components: {
    Product,
    AddToCartButton,
  },
  props: {
    title: String,
    products: Array,
    onSaleFilter: Boolean,
    toSendEvent: {
      type: Boolean,
      default: false,
    },
    swiperMargin: {
      type: Boolean,
      default: true,
    },
    slidesPerView: {
      type: Number,
      default: 4.75,
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    slideBreakpoints: {
      type: Object,
      validator: (breakpoints) => validateSlideBreakpoints(breakpoints),
    },
    spaceBetween: {
      type: Number,
      default: 80,
    },
    smallArrows: {
      type: Boolean,
      default: false,
    },
    carouselPadding: {
      type: Number,
      default: null,
    },
    showAddToCartBtn: {
      type: Boolean,
      default: false,
    },
    animateCartBtn: {
      type: Boolean,
      default: false,
    },
    openInNewTab: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      id: null,
      carousel: null,
      toShow: true,
      displayedProducts: [],
      initialNumber: 5,
      loaded: false,
    };
  },
  computed: {
    productsWithoutVariation() {
      if (this.products) {
        return this.products.map((product) => ({ ...product, variations: null }));
      }
      return null;
    },
  },
  created() {
    if (this.productsWithoutVariation && this.productsWithoutVariation.length) {
      this.displayedProducts = this.productsWithoutVariation.slice(0, this.initialNumber);
    }
  },
  mounted() {
    this.id = this._uid;

    if ('requestIdleCallback' in window) {
      this.loadRest();
      this.setUpSwiper();
    } else {
      setTimeout(() => {
        this.loadRest();
        this.setUpSwiper();
      });
    }
  },
  methods: {
    async setUpSwiper() {
      const Swiper = await ImportUtils.getSwiper();
      this.carousel = new Swiper(this.$refs.swiper, {
        observer: true,
        observeParents: true,
        freeMode: false,
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: this.slideBreakpoints || {
          1366: {
            slidesPerView: 3.75,
            spaceBetween: 80,
          },
          959: {
            slidesPerView: 2.75,
            spaceBetween: 40,
          },
          767: {
            slidesPerView: 1.65,
            spaceBetween: 20,
          },
        },
        pagination: {
          el: this.$refs.pagination,
          clickable: true,
          type: 'bullets',
          dynamicBullets: true,
        },
        on: {
          init: () => {
            this.$nextTick(() => {
              this.toShow = true;
              this.$emit('loaded');
            });
          },
          slideChange: () => {
            this.sendEvent();
          },
        },
      });

      this.sendEvent();
    },

    sendEvent() {
      if (this.toSendEvent && this.carousel && this.carousel.slides) {
        const slide = this.carousel.slides[this.carousel.activeIndex];

        if (slide) {
          Tracking.track('product_carousel_index', {
            product_id: slide.getAttribute('data-product-id'),
            carousel_index: this.carousel.activeIndex,
          });
        }
      }
    },

    loadRest() {
      if (this.loaded || (this.productsWithoutVariation && this.productsWithoutVariation.length < this.initialNumber)) {
        return;
      }

      this.displayedProducts = [...this.displayedProducts, ...this.productsWithoutVariation.slice(this.initialNumber)];

      this.$nextTick(() => {
        if (this.carousel) {
          this.carousel.destroy();
          this.carousel = null;
          this.setUpSwiper();
        }
      });

      this.loaded = true;
    },

    onProductAddedToCart(product) {
      this.$emit('productAddedToCart', product);
    },

    parseProduct(product) {
      return [{ id: product.id, quantity: product.packagedItemQuantity }];
    },

    onProductClick() {
      this.$emit('productClicked');
    },
  },

  watch: {
    products: {
      handler(products) {
        if (this.carousel && products && products.length) {
          this.displayedProducts = [...this.productsWithoutVariation];
          this.carousel.slideTo(0, 500, false);
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';
@import '~swiper/dist/css/swiper.min.css';

.product-carousel-container {
  min-height: 220px;

  h1 {
    padding-bottom: 15px;
  }

  &.swiper-margin {
    margin-top: 40px;
    margin-bottom: 40px;
  }

  .product-carousel-title {
    text-transform: uppercase;
    text-align: left;
    margin-bottom: var(--space-2);
  }

  .product-container {
    .favorite {
      top: 4px;
      right: 4px;
    }
  }

  .swiper-slide {
    .browse-heading {
      font-size: var(--font-size-14);
    }

    .price {
      font-size: 1.1em;
      color: var(--coral);
    }

    .favorite {
      top: -8px !important;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    visibility: hidden;
    opacity: 0;
    transition: visibility 0s linear 100ms, opacity 100ms ease-in;
    background-image: none;
    display: flex;
    font-size: 1em;
    width: 32px;
    height: 32px;
    top: 40%;
    padding: 8px;
    color: var(--link-color);
    margin-top: inherit;
    background-color: $white;
    justify-content: center;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    cursor: pointer;

    &.small-arrow {
      width: 22px;
      height: 22px;
    }

    @media (max-width: $tablet-breakpoint - 1) {
      display: none;
    }

    .af {
      align-self: center;
      padding: 10px;
    }
  }

  .swiper-button-next:not(.initial-position) {
    right: 16px;
  }

  .swiper-button-prev:not(.initial-position) {
    left: 16px;
  }

  &:hover {
    .swiper-button-next,
    .swiper-button-prev {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s linear, opacity 200ms ease-out;

      &.swiper-button-disabled {
        pointer-events: initial;
        opacity: 50%;
      }
    }
  }

  .swiper-pagination {
    margin-top: var(--space-6);
    position: relative;
    left: 50%;

    .swiper-pagination-bullet-active {
      background: var(--coral) !important;
    }
  }

  .add-to-cart-button {
    button,
    .button {
      display: flex;
      background-color: var(--white);
    }

    button {
      font-size: 14px;
      padding: 8px 12px;

      @media (max-width: $mobile-breakpoint - 1) {
        font-size: 1em;
        padding: 10px 12px;
      }
    }

    .button {
      justify-content: center;
      padding: 6px 12px;

      @media (max-width: $mobile-breakpoint - 1) {
        padding: 9px 12px;
      }
    }
  }
}
</style>
